/**
 * Trend Feature Constants — portable subset safe for web and mobile.
 *
 * Excludes web-only color/style tokens and the `@/lib/time` wrapper; uses
 * plain Date arithmetic instead so this file has zero external runtime deps.
 *
 * @see apps/web/lib/trends/constants.ts for the full web version (includes
 * CATEGORY_COLORS, TREND_DIRECTION_COLORS, and the dayjs-backed helpers).
 */

import type { FunctionalSystem } from "./types";

// -----------------------------------------------------------------------------
// Category Definitions
// -----------------------------------------------------------------------------

/**
 * All available trend categories — the 9 functional categories from the Figma design.
 * Listed alphabetically by display label: this array's order is the canonical render
 * order for the category picker/dropdown and the "By category" section headers.
 * @see https://www.figma.com/design/pqY81U2nOTtJeHfVVHpk2Q/EHR-Trends-Exploration?node-id=2650-3328
 */
export const TREND_CATEGORIES = [
  "autonomic",
  "cardiovascular",
  "digestive",
  "endocrine",
  "inflammation",
  "metabolism",
  "muscular_skeletal",
  "nutrients",
  "sleep",
] as const;

export type TrendCategory = (typeof TREND_CATEGORIES)[number];
export const TREND_CATEGORIES_WITH_OTHER = [...TREND_CATEGORIES, "other"] as const;
export type TrendCategoryWithOther = (typeof TREND_CATEGORIES_WITH_OTHER)[number];

/**
 * Functional systems the "Vitals" filter narrows connected-device data to —
 * heart rate, HRV, blood pressure, SpO2, respiratory rate, and temperature all
 * resolve to these. Shared so the web and mobile Trends filters can't drift.
 */
export const VITALS_FUNCTIONAL_SYSTEMS: readonly FunctionalSystem[] = [
  "cardiovascular",
  "autonomic",
];

/** Human-readable category labels. */
export const CATEGORY_LABELS: Record<TrendCategoryWithOther, string> = {
  metabolism: "Metabolism",
  cardiovascular: "Cardiovascular",
  autonomic: "Autonomic",
  inflammation: "Inflammation",
  endocrine: "Endocrine",
  muscular_skeletal: "Musculoskeletal",
  sleep: "Sleep",
  nutrients: "Nutrients",
  digestive: "Digestive",
  other: "Other",
};

/**
 * Category icons (Tabler icon names).
 * @see https://tabler.io/icons
 */
export const CATEGORY_ICONS: Record<TrendCategoryWithOther, string> = {
  metabolism: "flame",
  cardiovascular: "heart",
  autonomic: "activity-heartbeat",
  inflammation: "shield",
  endocrine: "flask",
  muscular_skeletal: "bone",
  sleep: "zzz",
  nutrients: "leaf",
  digestive: "apple",
  other: "vial",
};

// -----------------------------------------------------------------------------
// LOINC Code Mappings
// -----------------------------------------------------------------------------

/**
 * Common LOINC codes used in trends — wearable-derived biomarkers.
 * For the full registry with display names/units, use VitalLOINCCodes from
 * @vibrant/models/wearables/loinc.
 */
export const LOINC_CODES = {
  // Cardiovascular
  HRV: "80404-7",
  RESTING_HEART_RATE: "40443-4",
  HEART_RATE: "8867-4",

  // Respiratory
  SPO2: "59408-5",
  RESPIRATORY_RATE: "9279-1",

  // Sleep
  SLEEP_DURATION: "93832-4",
  SLEEP_EFFICIENCY: "93831-6",

  // Activity
  STEPS: "55423-8",
  ACTIVE_CALORIES: "41981-2",
  DISTANCE: "55430-3",

  // Body Composition
  WEIGHT: "29463-7",
  BMI: "39156-5",
  BODY_FAT: "41982-0",

  // Metabolic
  GLUCOSE: "2339-0",
  CGM_GLUCOSE: "99504-3",
} as const;

export type LoincCodeKey = keyof typeof LOINC_CODES;
export type LoincCode = (typeof LOINC_CODES)[LoincCodeKey];

/** Reverse mapping: LOINC code → key name. */
export const LOINC_CODE_TO_KEY: Record<string, LoincCodeKey> = Object.entries(LOINC_CODES).reduce(
  (acc, [key, code]) => {
    acc[code] = key as LoincCodeKey;
    return acc;
  },
  {} as Record<string, LoincCodeKey>,
);

/**
 * LOINC codes grouped by category (wearable biomarkers only).
 * For the complete mapping including labs, see BIOMARKER_CONFIG.
 */
export const LOINC_CODES_BY_CATEGORY: Record<TrendCategory, LoincCode[]> = {
  metabolism: [
    LOINC_CODES.GLUCOSE,
    LOINC_CODES.CGM_GLUCOSE,
    LOINC_CODES.STEPS,
    LOINC_CODES.ACTIVE_CALORIES,
    LOINC_CODES.DISTANCE,
    LOINC_CODES.WEIGHT,
    LOINC_CODES.BMI,
    LOINC_CODES.BODY_FAT,
  ],
  cardiovascular: [
    LOINC_CODES.HRV,
    LOINC_CODES.RESTING_HEART_RATE,
    LOINC_CODES.HEART_RATE,
    LOINC_CODES.SPO2,
  ],
  autonomic: [LOINC_CODES.HRV, LOINC_CODES.RESPIRATORY_RATE],
  inflammation: [],
  endocrine: [],
  muscular_skeletal: [],
  sleep: [LOINC_CODES.SLEEP_DURATION, LOINC_CODES.SLEEP_EFFICIENCY],
  nutrients: [],
  digestive: [],
};

// -----------------------------------------------------------------------------
// Default Date Ranges
// -----------------------------------------------------------------------------

/** Preset date range options for the UI (number of days). */
export const DATE_RANGE_PRESETS = {
  WEEK: 7,
  TWO_WEEKS: 14,
  MONTH: 30,
  THREE_MONTHS: 90,
  SIX_MONTHS: 180,
  YEAR: 365,
} as const;

export type DateRangePreset = keyof typeof DATE_RANGE_PRESETS;

/** Human-readable labels for date range presets. */
export const DATE_RANGE_LABELS: Record<DateRangePreset, string> = {
  WEEK: "Last 7 days",
  TWO_WEEKS: "Last 2 weeks",
  MONTH: "Last 30 days",
  THREE_MONTHS: "Last 3 months",
  SIX_MONTHS: "Last 6 months",
  YEAR: "Last year",
};

/**
 * The "all-time" / "Max" trend window, in years. Trends fetch this far back so a
 * biomarker whose most recent reading is old still surfaces (labs especially);
 * the chart axis then clamps to the actual data. Shared by the web overview, the
 * web detail "Max" range, and the mobile overview so they stay in lockstep.
 */
export const ALL_TIME_TREND_YEARS = 50;

/**
 * Max length of a patient Trends feedback message. The client input (web + mobile), the web
 * server action, and the mobile BFF route all enforce this so the UI can never submit more than
 * the server accepts. Shared here so web and mobile stay in lockstep.
 */
export const TRENDS_FEEDBACK_MAX_MESSAGE_LENGTH = 2000;

/** Zero-pad a number to 2 digits. */
function padZero(n: number): string {
  return n.toString().padStart(2, "0");
}

/**
 * Format a Date as YYYY-MM-DD using UTC methods so that dates constructed
 * from ISO strings (e.g. `new Date("2025-01-10")` which is midnight UTC) are
 * serialised correctly regardless of the runtime's local timezone offset.
 */
function formatYMD(d: Date): string {
  return `${d.getUTCFullYear()}-${padZero(d.getUTCMonth() + 1)}-${padZero(d.getUTCDate())}`;
}

/**
 * Calculate a date range from a number of days back.
 * Uses an inclusive window: the end date counts as day 1
 * (e.g., days=7 → today + 6 prior days).
 *
 * All arithmetic is performed in UTC to avoid DST / timezone edge-cases.
 *
 * @param days - Number of days in the window (1–365)
 * @param referenceDate - The end date (defaults to today in UTC)
 * @returns Object with start and end YYYY-MM-DD date strings
 */
export function getDateRangeFromDays(
  days: number,
  referenceDate: Date = new Date(),
): { start: string; end: string } {
  if (!Number.isInteger(days) || days < 1 || days > 365) {
    throw new RangeError("days must be an integer between 1 and 365");
  }

  // Work in UTC milliseconds to avoid DST shifts
  const endMs = Date.UTC(
    referenceDate.getUTCFullYear(),
    referenceDate.getUTCMonth(),
    referenceDate.getUTCDate(),
  );
  const startMs = endMs - (days - 1) * 86_400_000;
  return {
    start: formatYMD(new Date(startMs)),
    end: formatYMD(new Date(endMs)),
  };
}

/**
 * Get a date range from a preset.
 *
 * @param preset - The preset to use
 * @param referenceDate - The end date (defaults to today)
 * @returns Object with start and end YYYY-MM-DD date strings
 */
export function getDateRangeFromPreset(
  preset: DateRangePreset,
  referenceDate: Date = new Date(),
): { start: string; end: string } {
  return getDateRangeFromDays(DATE_RANGE_PRESETS[preset], referenceDate);
}

// -----------------------------------------------------------------------------
// Trend Direction
// -----------------------------------------------------------------------------

export type TrendDirection = "increasing" | "decreasing" | "stable";

/** Icons for trend directions (Tabler icon names). */
export const TREND_DIRECTION_ICONS: Record<TrendDirection, string> = {
  increasing: "arrow-up",
  decreasing: "arrow-down",
  stable: "minus",
};

// -----------------------------------------------------------------------------
// Provider / Source Labels
// -----------------------------------------------------------------------------

/** Known wearable data provider slugs. */
export const DATA_PROVIDERS = ["oura", "apple_health_kit", "whoop", "garmin", "fitbit"] as const;

export type DataProvider = (typeof DATA_PROVIDERS)[number];

/** Human-readable labels for wearable data providers. */
export const PROVIDER_LABELS: Record<DataProvider, string> = {
  oura: "Oura Ring",
  apple_health_kit: "Apple Health",
  whoop: "WHOOP",
  garmin: "Garmin",
  fitbit: "Fitbit",
};

/** Slug for clinic/note-editor vitals — highest priority during deduplication. */
export const CLINIC_PROVIDER_SLUG = "clinic" as const;

/** Label used for clinic-entered (manually recorded) observations across all views. */
export const CLINIC_PROVIDER_LABEL = "Manual";

/**
 * Returns the human-readable label for any provider slug.
 * Returns "" for unrecognised slugs so callers can apply their own fallback.
 */
export function getProviderLabel(slug: string | undefined): string {
  if (!slug) return "";
  if (slug === CLINIC_PROVIDER_SLUG) return CLINIC_PROVIDER_LABEL;
  return PROVIDER_LABELS[slug as DataProvider] ?? "";
}
